/* global window console fetch setTimeout */
import { batchGroupBy } from "./batchGroupBy";
import { isEmbeddableCrossOriginIframe } from "./embedBridge";
import { ActionCreators as UndoActionCreators } from "redux-undo";
import { reset } from "../state/structureSlice";
import { reset as styleReset } from "../state/cellStylesSlice";
import { reset as spacerStylesReset } from "../state/spacerStylesSlice";
import { init } from "../state/bordersSlice";
import { loadSheets } from "../state/styleSheetSlice";
import { SNAPSHOT_REQUEST_TYPE, handleSnapshotRequest } from "./snapshot";
import store from "../state/store";

// When embeddable.html is rendered inside a cross-origin iframe (e.g. the
// Gridd embed at azurestaticapps.net hosted inside a Docusaurus page on
// localhost), the browser blocks direct fetches to the parent's origin via
// CORS. The parent has same-origin access to those assets, so we ask it to
// fetch on our behalf via postMessage. This bridge is only activated when
// the page is embeddable.html AND the parent frame is cross-origin.

const PROXY_REQUEST_TYPE = "gridd-proxy-fetch";
const PROXY_RESULT_TYPE = "gridd-proxy-fetch-result";
const INLINE_REQUEST_TYPE = "gridd-inline-request";
const INLINE_RESPONSE_TYPE = "gridd-inline-response";
// Stylesheet bridge: the host frame can push a stylesheet at runtime (live
// preview) or answer the editor page's pull. Payload is the { selectedSheet,
// sheets } shape loadSheets already accepts (same as gridd_style_uri JSON).
const STYLESHEET_PUSH_TYPE = "gridd-stylesheet-push";
const STYLESHEET_REQUEST_TYPE = "gridd-stylesheet-request";
const STYLESHEET_RESPONSE_TYPE = "gridd-stylesheet-response";
const PROXY_TIMEOUT_MS = 10000;

let pendingProxyId = 0;
const pendingProxyResolvers = new Map();
let pendingInlineId = 0;
const pendingInlineResolvers = new Map();
let pendingStyleSheetId = 0;
const pendingStyleSheetResolvers = new Map();

if (typeof window !== "undefined") {
  window.addEventListener("message", (event) => {
    const data = event.data;
    if (!data || typeof data !== "object") return;
    if (data.type === PROXY_RESULT_TYPE) {
      const resolver = pendingProxyResolvers.get(data.id);
      if (!resolver) return;
      pendingProxyResolvers.delete(data.id);
      resolver(data);
    } else if (data.type === INLINE_RESPONSE_TYPE) {
      const resolver = pendingInlineResolvers.get(data.id);
      if (!resolver) return;
      pendingInlineResolvers.delete(data.id);
      // event.origin is the browser-supplied, unforgeable origin of the parent
      // frame — the authoritative signal for the editable-host decision.
      resolver({ result: data, origin: event.origin });
    } else if (data.type === STYLESHEET_PUSH_TYPE) {
      // Host pushed a stylesheet at runtime — apply it live to whatever view is
      // mounted (gridd or the editor panel). Additive: unknown to Office/PPT.
      if (data.payload) store.dispatch(loadSheets(data.payload));
    } else if (data.type === STYLESHEET_RESPONSE_TYPE) {
      const resolver = pendingStyleSheetResolvers.get(data.id);
      if (!resolver) return;
      pendingStyleSheetResolvers.delete(data.id);
      resolver(data);
    } else if (data.type === SNAPSHOT_REQUEST_TYPE) {
      // Host asked for a static raster of the live grid (deck print path).
      // Fire-and-forget: handleSnapshotRequest posts back its own
      // gridd-snapshot-response / gridd-snapshot-error and never throws.
      handleSnapshotRequest(data, event);
    }
  });
}

function fetchViaParent(uri) {
  return new Promise((resolve, reject) => {
    const id = ++pendingProxyId;
    pendingProxyResolvers.set(id, (result) => {
      if (result.ok) resolve(result.body);
      else reject(new Error(result.error || `HTTP ${result.status}`));
    });
    setTimeout(() => {
      if (pendingProxyResolvers.delete(id)) {
        reject(new Error(`Parent proxy fetch timed out for ${uri}`));
      }
    }, PROXY_TIMEOUT_MS);
    window.parent.postMessage({ type: PROXY_REQUEST_TYPE, id, url: uri }, "*");
  });
}

export function requestInlineDataFromParent() {
  return new Promise((resolve, reject) => {
    const id = ++pendingInlineId;
    pendingInlineResolvers.set(id, ({ result, origin }) => {
      if (result.ok) resolve({ data: result.data, origin });
      else reject(new Error(result.error || "Inline data request failed"));
    });
    setTimeout(() => {
      if (pendingInlineResolvers.delete(id)) {
        reject(new Error("Inline data request from parent timed out"));
      }
    }, PROXY_TIMEOUT_MS);
    window.parent.postMessage({ type: INLINE_REQUEST_TYPE, id }, "*");
  });
}

// Pull the current stylesheet from the host frame. The editor panel route
// bypasses Startup, so it uses this on mount to seed loadSheets rather than
// showing built-in defaults. Resolves with the { selectedSheet, sheets } payload.
export function requestStyleSheetFromParent() {
  return new Promise((resolve, reject) => {
    const id = ++pendingStyleSheetId;
    pendingStyleSheetResolvers.set(id, (result) => {
      if (result.ok) resolve(result.payload);
      else reject(new Error(result.error || "Stylesheet request failed"));
    });
    setTimeout(() => {
      if (pendingStyleSheetResolvers.delete(id)) {
        reject(new Error("Stylesheet request from parent timed out"));
      }
    }, PROXY_TIMEOUT_MS);
    window.parent.postMessage({ type: STYLESHEET_REQUEST_TYPE, id }, "*");
  });
}

// The origin trust boundary lives in ./allowedOrigins so the snapshot path can
// share the same allowlist without a circular import. Re-exported here to keep
// existing import sites (Startup.jsx, etc.) working unchanged.
export { isLocalhostUri, isEditableInlineOrigin } from "./allowedOrigins";

export function loadGriddObject(dispatch, griddObject) {
  batchGroupBy.start();
  dispatch(
    reset({
      columns: griddObject.cols,
      rows: griddObject.rows,
      cells: griddObject.cells,
      merged: griddObject.merged,
    })
  );
  dispatch(init(griddObject.borders));
  if (griddObject.cellStyles) {
    dispatch(styleReset(griddObject.cellStyles));
  }
  if (griddObject.spacerStyles) {
    dispatch(spacerStylesReset(griddObject.spacerStyles));
  }
  batchGroupBy.end();
  dispatch(UndoActionCreators.clearHistory());
}

export async function loadFromAssets(griddDataId) {
  try {
    const response = await fetch(`gridd_data/${griddDataId}.json`);
    if (!response.ok) {
      console.error(`Error fetching Gridd data for ID: ${griddDataId}, HTTP Status: ${response.status}`);
      return null;
    }
    return await response.text();
  } catch (error) {
    console.error(`Error processing Gridd data for ID: ${griddDataId}. Error:`, error);
    return null;
  }
}

export async function loadFromURI(uri) {
  try {
    if (isEmbeddableCrossOriginIframe()) {
      console.log(`embeddable.html: fetching ${uri} via parent proxy (CORS bypass)`);
      return await fetchViaParent(uri);
    }
    const response = await fetch(uri);
    if (!response.ok) {
      console.error(`Error fetching Gridd data for URI: ${uri}, HTTP Status: ${response.status}`);
      return null;
    }
    return await response.text();
  } catch (error) {
    console.error(`Error processing Gridd data for URI: ${uri}. Error:`, error);
    return null;
  }
}
